import { useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import { Loader2, Upload, Trash2, Download, ArrowLeft, CheckSquare, Square, Package } from 'lucide-react'
import Modal from '../../../components/Modal/Modal'
import DeviceStatusBadge from '../../../components/DeviceStatusBadge'
import {
  useMdmCatalogApps, useUploadMdmCatalogApp, useDeleteMdmCatalogApp, useInstallMdmCatalogApp,
} from '../../../hooks/useMdm'
import { formatDateTime } from '../../../utils/format'
import { errorMessage, formatBytes } from './mobileUtils'

/**
 * Catálogo de apps gerenciados do tenant (CONTRACT.md §7.1): sobe o APK uma vez, instala em N
 * tablets e, opcionalmente, define como app de quiosque.
 *
 * devices: itens da frota ({ id, name, status }); preselectedIds: seleção atual da frota.
 * onFeedback({ type: 'ok'|'error', text }): mensagem exibida pela tela da frota.
 */
export default function ManagedAppsModal({
  isOpen, onClose, devices = [], preselectedIds = [], onFeedback,
  scopeGroupId = null, scopeName = 'todos os tablets', scopeCount = 0,
}) {
  const { data: apps = [], isLoading, error } = useMdmCatalogApps(isOpen)
  const upload = useUploadMdmCatalogApp()
  const remove = useDeleteMdmCatalogApp()
  const install = useInstallMdmCatalogApp()
  const fileRef = useRef(null)

  const [file, setFile] = useState(null)
  const [label, setLabel] = useState('')
  const [progress, setProgress] = useState(null)
  const [msg, setMsg] = useState(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState(null)

  // Passo "Instalar…": app escolhido + devices marcados + quiosque.
  const [installApp, setInstallApp] = useState(null)
  const [targets, setTargets] = useState(() => new Set())
  const [kiosk, setKiosk] = useState(false)
  // true = todos os tablets do escopo (grupo + subgrupos), resolvidos no servidor.
  const [wholeScope, setWholeScope] = useState(false)

  const sortedApps = useMemo(
    () => [...apps].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [apps]
  )

  const close = () => {
    setInstallApp(null)
    setMsg(null)
    setConfirmDeleteId(null)
    onClose?.()
  }

  const pickFile = (e) => {
    const f = e.target.files?.[0]
    e.target.value = ''
    if (!f) return
    if (!f.name.toLowerCase().endsWith('.apk')) {
      setMsg({ ok: false, text: 'Selecione um arquivo .apk.' })
      return
    }
    setMsg(null)
    setFile(f)
  }

  const handleUpload = async () => {
    if (!file) return
    setMsg(null)
    setProgress(0)
    try {
      const app = await upload.mutateAsync({ file, label, onProgress: setProgress })
      setMsg({ ok: true, text: `${app.label || app.packageName} ${app.versionName || ''} adicionado ao catálogo.` })
      setFile(null)
      setLabel('')
    } catch (e) {
      setMsg({ ok: false, text: errorMessage(e, 'Falha ao enviar o APK.') })
    } finally {
      setProgress(null)
    }
  }

  const handleDelete = async (app) => {
    setMsg(null)
    try {
      await remove.mutateAsync(app.id)
      setConfirmDeleteId(null)
      setMsg({ ok: true, text: `${app.label || app.packageName} removido do catálogo (continua instalado nos tablets).` })
    } catch (e) {
      setMsg({ ok: false, text: errorMessage(e, 'Falha ao remover o app.') })
    }
  }

  const openInstall = (app) => {
    const known = new Set(devices.map((d) => d.id))
    const pre = preselectedIds.filter((id) => known.has(id))
    setTargets(new Set(pre))
    setWholeScope(pre.length === 0 && scopeCount > 0)
    setKiosk(false)
    setMsg(null)
    setInstallApp(app)
  }

  const toggleTarget = (id) => {
    setTargets((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const allTargets = devices.length > 0 && devices.every((d) => targets.has(d.id))
  const toggleAllTargets = () => setTargets(allTargets ? new Set() : new Set(devices.map((d) => d.id)))

  const handleInstall = async () => {
    if (!installApp || (!wholeScope && targets.size === 0)) return
    setMsg(null)
    try {
      const created = await install.mutateAsync(wholeScope
        ? { appId: installApp.id, allInScope: true, groupId: scopeGroupId, kiosk }
        : { appId: installApp.id, deviceIds: [...targets], kiosk })
      const n = Array.isArray(created) ? created.length : targets.size
      const name = installApp.label || installApp.packageName
      onFeedback?.({
        type: 'ok',
        text: `Instalação de ${name} enviada para ${n} dispositivo(s)${wholeScope ? ` — ${scopeName}` : ''}${kiosk ? (wholeScope ? ' — e definido como quiosque na política do grupo' : ' — e definido como quiosque') : ''}.`,
      })
      close()
    } catch (e) {
      setMsg({ ok: false, text: errorMessage(e, 'Falha ao solicitar a instalação.') })
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={close} title={installApp ? `Instalar ${installApp.label || installApp.packageName}` : 'Apps gerenciados'}>
      {installApp ? (
        <div className="space-y-4">
          <button type="button" onClick={() => setInstallApp(null)} className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900">
            <ArrowLeft className="w-4 h-4" /> Voltar ao catálogo
          </button>

          <div className="text-sm text-gray-600">
            <span className="font-mono">{installApp.packageName}</span> · versão {installApp.versionName || '—'} ({installApp.versionCode}) · {formatBytes(installApp.size)}
          </div>

          <div className="flex flex-col gap-1 text-sm text-gray-700">
            <label className="flex items-center gap-2">
              <input type="radio" checked={wholeScope} onChange={() => setWholeScope(true)} disabled={scopeCount === 0} />
              Todos de {scopeName} ({scopeCount}{scopeGroupId ? ', com subgrupos' : ''})
            </label>
            <label className="flex items-center gap-2">
              <input type="radio" checked={!wholeScope} onChange={() => setWholeScope(false)} />
              Escolher tablets
            </label>
          </div>

          {!wholeScope && (
          <div className="border border-gray-200 rounded-lg">
            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 bg-gray-50">
              <button type="button" onClick={toggleAllTargets} className="flex items-center gap-1 text-sm text-gray-700">
                {allTargets ? <CheckSquare className="w-4 h-4 text-theme-primary" /> : <Square className="w-4 h-4" />}
                Todos
              </button>
              <span className="text-xs text-gray-500">{targets.size} de {devices.length} selecionado(s)</span>
            </div>
            <div className="max-h-64 overflow-y-auto divide-y divide-gray-100">
              {devices.length === 0 ? (
                <p className="px-3 py-4 text-sm text-gray-500 text-center">Nenhum tablet nesta visão.</p>
              ) : devices.map((d) => (
                <label key={d.id} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer">
                  <input type="checkbox" checked={targets.has(d.id)} onChange={() => toggleTarget(d.id)} />
                  <span className="flex-1 truncate text-gray-900">{d.name}</span>
                  <DeviceStatusBadge status={d.status} />
                </label>
              ))}
            </div>
          </div>
          )}

          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input type="checkbox" className="mt-0.5" checked={kiosk} onChange={(e) => setKiosk(e.target.checked)} />
            <span>
              Definir como quiosque (o tablet passa a abrir só este app)
              <span className="block text-xs text-gray-500">
                {wholeScope
                  ? 'Grava na política do grupo (tablets com política própria não mudam); o quiosque começa assim que a instalação terminar.'
                  : 'Grava na política de cada tablet; o quiosque começa assim que a instalação terminar.'}
              </span>
            </span>
          </label>

          {msg && <p className={clsx('text-sm', msg.ok ? 'text-green-700' : 'text-red-600')}>{msg.text}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => setInstallApp(null)} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleInstall}
              disabled={(!wholeScope && targets.size === 0) || install.isPending}
              className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-theme-primary rounded-lg hover:bg-theme-primary/90 disabled:opacity-50"
            >
              {install.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Instalar em {wholeScope ? scopeCount : targets.size} dispositivo(s)
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Upload */}
          <div className="border border-dashed border-gray-300 rounded-lg p-3 space-y-2">
            <div className="text-sm font-medium text-gray-800">Adicionar APK ao catálogo</div>
            <p className="text-xs text-gray-500">Pacote e versão são lidos do próprio APK. Use sempre o APK de release assinado com a chave definitiva do app.</p>
            <div className="flex flex-wrap items-center gap-2">
              <input ref={fileRef} type="file" accept=".apk,application/vnd.android.package-archive" className="hidden" onChange={pickFile} />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={upload.isPending}
                className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                <Package className="w-4 h-4" /> {file ? 'Trocar arquivo' : 'Escolher APK'}
              </button>
              {file && <span className="text-sm text-gray-700 truncate max-w-[16rem]" title={file.name}>{file.name} · {formatBytes(file.size)}</span>}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Rótulo (opcional), ex.: App do motorista TFD"
                className="flex-1 min-w-[12rem] border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                disabled={upload.isPending}
              />
              <button
                type="button"
                onClick={handleUpload}
                disabled={!file || upload.isPending}
                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-theme-primary text-white rounded-lg hover:bg-theme-primary/90 disabled:opacity-50"
              >
                {upload.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {progress != null ? (progress < 100 ? `Enviando ${progress}%` : 'Processando…') : 'Enviar'}
              </button>
            </div>
            {progress != null && (
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-theme-primary transition-all" style={{ width: `${progress}%` }} />
              </div>
            )}
          </div>

          {msg && <p className={clsx('text-sm', msg.ok ? 'text-green-700' : 'text-red-600')}>{msg.text}</p>}
          {error && <p className="text-sm text-red-600">{errorMessage(error, 'Erro ao carregar o catálogo.')}</p>}

          {/* Catálogo */}
          <div className="border border-gray-200 rounded-lg overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="text-left px-3 py-2">App</th>
                  <th className="text-left px-3 py-2">Versão</th>
                  <th className="text-left px-3 py-2">Tamanho</th>
                  <th className="text-left px-3 py-2">Enviado</th>
                  <th className="text-right px-3 py-2">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {isLoading ? (
                  <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Carregando...</td></tr>
                ) : sortedApps.length === 0 ? (
                  <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-500">Nenhum app no catálogo ainda.</td></tr>
                ) : sortedApps.map((a) => (
                  <tr key={a.id} className="hover:bg-gray-50 align-top">
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-900">{a.label || a.packageName}</div>
                      <div className="text-xs text-gray-500 font-mono">{a.packageName}</div>
                    </td>
                    <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                      {a.versionName || '—'} <span className="text-xs text-gray-400">({a.versionCode})</span>
                    </td>
                    <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{formatBytes(a.size)}</td>
                    <td className="px-3 py-2 text-gray-600">
                      <div className="whitespace-nowrap">{a.createdAt ? formatDateTime(a.createdAt) : '—'}</div>
                      {a.uploadedBy && <div className="text-xs text-gray-400 truncate max-w-[10rem]" title={a.uploadedBy}>{a.uploadedBy}</div>}
                    </td>
                    <td className="px-3 py-2">
                      {confirmDeleteId === a.id ? (
                        <div className="flex justify-end items-center gap-1 text-xs">
                          <span className="text-gray-600">Remover?</span>
                          <button
                            type="button"
                            onClick={() => handleDelete(a)}
                            disabled={remove.isPending}
                            className="px-2 py-1 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                          >
                            {remove.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Sim'}
                          </button>
                          <button type="button" onClick={() => setConfirmDeleteId(null)} className="px-2 py-1 rounded-md border border-gray-300 hover:bg-gray-50">
                            Não
                          </button>
                        </div>
                      ) : (
                        <div className="flex justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => openInstall(a)}
                            className="flex items-center gap-1 px-2.5 py-1 text-xs border border-gray-300 bg-white rounded-md hover:bg-gray-50"
                          >
                            <Download className="w-3.5 h-3.5" /> Instalar…
                          </button>
                          <button
                            type="button"
                            title="Remover do catálogo"
                            onClick={() => setConfirmDeleteId(a.id)}
                            className="p-1.5 rounded-md text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Modal>
  )
}
